/**
 * 个股秒级行情 channel：基于 {@link RealtimeSubscriber} 实现的订阅式通信封装。
 *
 * 协议要点：
 * - 上行订阅：`{ type: 'stock_list_push', timestamp, param: { fullCodes } }`，fullCode 归一为紧凑形式
 *   （美股保留原始大小写，A/港股小写；见 {@link normalizeStockFullCodeForSubscribe}）
 * - 保活：由 RealtimeSubscriber 在每次 flush 后按 5s 间隔轮询重发同一份订阅 payload
 * - 下行：`{ businessType: 'stock_list_push', data: { stockInfo: [...] } }`（stockInfo 项含 timeIndex 等字段）
 *
 * 业务侧抽象：
 * - {@link SecChannelSubscriber#subscribe} / {@link SecChannelSubscriber#unsubscribe} 维护 per-fullCode 引用计数；
 * - 引用计数变更触发 `_subscriber.requestFlush()`，进入「防抖 → flush → 5s 轮询」循环；
 * - 完全归零时自动 `destroy()` 释放 hub 订阅，下次再 subscribe 时自然重新 install。
 *
 * 模块层导出 lazy 单例 {@link getSecChannelSubscriber}：首次调用时实例化并冻结 callbacks，
 * 后续调用仅返回同一实例（避免 vuex action 重复 dispatch 时改写回调引用）。
 *
 * @module utils/realtime/stockChannels/secChannel
 */

import { isArray, isNil, isPlainObject } from 'lodash-es';
import { normalizeStockFullCodeForSubscribe, resolveStockFullCodeKey } from '@/utils/biz/stock';
import { normalizeStringList } from '@/utils/string';
import { normalizePushData, normalizeStockDetail } from '@/utils/realtime/normalizePushData';
import { WS_MESSAGE_TYPE } from '@/utils/realtime/wsMessageType';
import { RealtimeSubscriber } from '@/utils/realtime/RealtimeSubscriber';

/** SEC channel 标识（仅供本模块内部使用：channel id 字面量 / 回调参数） */
const SEC_CHANNEL_ID = 'sec';

/** SEC channel 提供的核心字段集合（含常用嵌套字段 tradeStatus 与美股盘前附属行情） */
const SEC_FIELDS = Object.freeze([
  'lastPrice',
  'changePct',
  'tradeStatus',
  'status',
  'timeIndex',
  'preLastPrice',
  'preChangePct'
]);

/**
 * 上行订阅 payload：替换式同步当前活跃 fullCodes（空集合也发，让服务端清理）。
 * @param {string[]} fullCodes 当前活跃的 fullCode 列表
 * @returns {{ type: string, timestamp: number, param: { fullCodes: string[] } }}
 */
function buildSubscribePayload(fullCodes) {
  const unique = normalizeStringList(fullCodes, {
    mapItem: normalizeStockFullCodeForSubscribe
  });
  return {
    type: WS_MESSAGE_TYPE.stock.STOCK_LIST_PUSH,
    timestamp: Date.now(),
    param: { fullCodes: unique }
  };
}

/**
 * 把单条服务端推送项映射为快照 `.quote` 的合并字段。
 * @param {Record<string, unknown> | undefined} item 单条下行明细
 * @returns {Record<string, unknown>}
 */
function mapStockInfoToQuotePatch(item) {
  if (!isPlainObject(item)) {
    return {};
  }
  const patch = normalizeStockDetail(item.stockDetail);
  // stockDetail 是主数据源；这里补充兼容顶层字段，避免服务端下行形态调整时业务层无感。
  const legacyKeys = [
    'lastPrice',
    'changePct',
    'status',
    'timestamp',
    'timeIndex',
    'yearChangePct',
    'tradeStatus',
    'tradingTime',
    'preLastPrice',
    'preChangePct'
  ];
  for (const key of legacyKeys) {
    if (!isNil(item[key])) {
      patch[key] = item[key];
    }
  }
  if (!isNil(patch.lastPrice)) {
    patch.price = patch.lastPrice;
  }
  return patch;
}

/**
 * 下行解析：先做 stock 域归一化，再按当前活跃 fullCodes 收敛到 `{ fullCode, patch }` 数组。
 * @param {unknown} data 下行消息 data 段
 * @param {Iterable<string>} activeFullCodes 当前活跃订阅的 fullCode 集合
 * @returns {Array<{ fullCode: string, patch: object }>}
 */
function parseSecPush(data, activeFullCodes) {
  const normalized = normalizePushData(data);
  const list = normalized.stockInfo;
  const rows = [];
  for (const item of list) {
    if (!isPlainObject(item)) {
      continue;
    }
    const fullCode = resolveStockFullCodeKey(item.fullCode || item.stockCode, activeFullCodes);
    if (!fullCode) {
      continue;
    }
    rows.push({ fullCode, patch: mapStockInfoToQuotePatch(item) });
  }
  return rows;
}

/**
 * 占位回调：未传 callback 时安全降级，避免业务侧 if 判断散落。
 * @returns {void}
 */
const noop = () => {};

/**
 * 个股秒级行情订阅器：维护 per-fullCode 引用计数，内部组合 {@link RealtimeSubscriber} 完成 hub 通信。
 */
export class SecChannelSubscriber {
  /** channel 标识 */
  static id = SEC_CHANNEL_ID;

  /** channel 提供的字段集合（业务侧 fields 反查会用到） */
  static fields = SEC_FIELDS;

  /**
   * fields 反查：业务侧字段集合是否落在本 channel 的字段范围内。
   * 任一命中即返回 true；用于 vuex 在多 channel 场景下决定是否转发到本 channel。
   * @param {string[]} fields 业务侧声明需要的字段
   * @returns {boolean}
   */
  static acceptFields(fields) {
    if (!isArray(fields) || !fields.length) {
      return false;
    }
    const fieldSet = new Set(SEC_FIELDS);
    for (const f of fields) {
      if (fieldSet.has(f)) {
        return true;
      }
    }
    return false;
  }

  /**
   * @param {Object} [callbacks]
   * @param {(fullCode: string, patch: object, channelId: string) => void} [callbacks.onPatch]
   * @param {(fullCode: string) => void} [callbacks.onFullCodeIdle]
   */
  constructor(callbacks = {}) {
    this._onPatch = callbacks.onPatch || noop;
    this._onFullCodeIdle = callbacks.onFullCodeIdle || noop;

    /** @type {Map<string, number>} fullCode -> 引用计数 */
    this._refCounts = new Map();

    this._subscriber = new RealtimeSubscriber({
      topic: WS_MESSAGE_TYPE.stock.STOCK_LIST_PUSH,
      buildSubscribePayload: () => buildSubscribePayload(this._activeFullCodes()),
      onPush: (data) => {
        // RealtimeSubscriber 已做基础 JSON.parse 兜底；这里继续完成 stock 域结构归一化与字段映射，
        // 然后逐条把 patch 投递给外部，由外部决定如何 merge / 触发 reactive 更新
        const rows = parseSecPush(data, this._activeFullCodes());
        for (const { fullCode, patch } of rows) {
          this._onPatch(fullCode, patch, SEC_CHANNEL_ID);
        }
      }
    });
  }

  /**
   * 增加订阅：每个 fullCode 引用计数 +1；首次进入活跃态时 install subscriber，
   * 否则仅 requestFlush 以触发新的防抖 → flush → 轮询循环。
   * @param {string[]} fullCodes 待订阅的 fullCode 列表
   * @returns {void}
   */
  subscribe(fullCodes) {
    const codes = normalizeStringList(fullCodes);
    if (!codes.length) {
      return;
    }
    const wasActive = this._subscriber.isInstalled();
    let needFlush = false;
    for (const code of codes) {
      const cur = this._refCounts.get(code) || 0;
      const next = cur + 1;
      this._refCounts.set(code, next);
      // 0 -> 1 表示首次订阅该 fullCode，需要把订阅集变更上送
      if (cur === 0) {
        needFlush = true;
      }
    }
    if (!wasActive) {
      // 首次进入活跃态：install 自身会立即 schedule 一次 flush
      this._subscriber.install();
      return;
    }
    if (needFlush) {
      this._subscriber.requestFlush();
    }
  }

  /**
   * 减少订阅：每个 fullCode 引用计数 -1；归零时移除并触发 onFullCodeIdle；
   * 若该 channel 上完全无订阅，则 destroy subscriber 释放 hub 订阅与 timer。
   * @param {string[]} fullCodes 待退订的 fullCode 列表
   * @returns {void}
   */
  unsubscribe(fullCodes) {
    const codes = normalizeStringList(fullCodes);
    if (!codes.length) {
      return;
    }
    let needFlush = false;
    for (const code of codes) {
      const cur = this._refCounts.get(code) || 0;
      if (cur <= 0) {
        // 没有引用计数可减，跳过避免负值
        continue;
      }
      const next = cur - 1;
      if (next <= 0) {
        this._refCounts.delete(code);
        needFlush = true;
        // 本 channel 上该 fullCode 已 idle；旧实现的 idle 语义是「所有 channel 都归零」，
        // 但当前架构下 sec 是唯一持有 fullCode 引用计数的 channel，单点归零等价于全局 idle
        this._onFullCodeIdle(code);
      } else {
        this._refCounts.set(code, next);
      }
    }
    if (!needFlush) {
      return;
    }
    if (this._refCounts.size === 0) {
      // 全部退订时主动释放 hub 订阅与 timer，避免空轮询；下次 subscribe 自然重新 install
      this._subscriber.destroy();
      return;
    }
    this._subscriber.requestFlush();
  }

  /**
   * 销毁：用于热替换 / 单测；释放 hub 订阅、timer 与 refCount 表。
   * @returns {void}
   */
  destroy() {
    this._subscriber.destroy();
    this._refCounts.clear();
  }

  /**
   * 当前仍有订阅的 fullCode 列表（字典序）。
   * @returns {string[]}
   */
  _activeFullCodes() {
    const out = [];
    for (const [code, n] of this._refCounts.entries()) {
      if (n > 0) {
        out.push(code);
      }
    }
    return out.sort();
  }
}

/** @type {SecChannelSubscriber | null} */
let secChannelSubscriberSingleton = null;

/**
 * 获取 SEC channel 的模块级 lazy 单例。
 *
 * - 首次调用时根据传入 callbacks 实例化并永久绑定（与旧 `installStockChannels` 一致的「首次安装即终身回调」语义）；
 * - 后续调用直接返回同一份，忽略再次传入的 callbacks，避免被重复 dispatch 改写回调引用。
 *
 * @param {ConstructorParameters<typeof SecChannelSubscriber>[0]} [callbacks]
 * @returns {SecChannelSubscriber}
 */
export function getSecChannelSubscriber(callbacks) {
  if (!secChannelSubscriberSingleton) {
    secChannelSubscriberSingleton = new SecChannelSubscriber(callbacks);
  }
  return secChannelSubscriberSingleton;
}
